import {
  DashboardMediaService,
  FeatureFlagName,
  MediaRestControllerService,
} from '@api'
import {
  attach,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'

import { $creatorId } from '@/models/creators.ts'
import { $availableFeatures } from '@/models/feature-flags.ts'
import { apiClient } from '@/utils/axios.ts'
import { isFeatureAvailable } from '@/utils/featureFlags.ts'

interface LoadMediaAttributesParams {
  mediaId: number
  creatorId: number
}

export const loadMediaAttributes = createEvent<number>('load media attributes')
export const setMediaLabel = createEvent<{ mediaId: number; label: string }>(
  'set media label',
)
export const resetMediaLabels = createEvent('reset media labels')

export const $mediaLabels = createStore<Record<number, string>>(
  {},
  {
    name: '$mediaLabels',
  },
)

export const loadMediaAttributesFx = attach({
  name: 'load media attributes',
  source: {
    creatorId: $creatorId,
    availableFeatures: $availableFeatures,
    mediaLabels: $mediaLabels,
  },
  mapParams: (
    mediaId: number,
    { creatorId, availableFeatures, mediaLabels },
  ) => {
    const isAIFeatureAvailable = isFeatureAvailable(
      availableFeatures,
      FeatureFlagName.AI_CHATTING_FEATURE,
    )

    return {
      mediaId,
      creatorId: creatorId!,
      shouldSkip: !!mediaLabels[mediaId] || !isAIFeatureAvailable,
    }
  },
  effect: createEffect<
    LoadMediaAttributesParams & { shouldSkip: boolean },
    { mediaId: number; label?: string } | null
  >(async ({ mediaId, creatorId, shouldSkip }) => {
    if (shouldSkip) {
      return null
    }

    try {
      const response = await DashboardMediaService.getMediaAttributes({
        creatorId,
        mediaId,
      })

      return {
        mediaId,
        label: response.label,
      }
    } catch (error) {
      console.error('Failed to load media attributes:', error)
      throw error
    }
  }),
})

$mediaLabels
  .on(setMediaLabel, (state, { mediaId, label }) => ({
    ...state,
    [mediaId]: label,
  }))
  .on(loadMediaAttributesFx.doneData, (state, response) => {
    if (!response?.label || !response.mediaId) return state

    return {
      ...state,
      [response.mediaId]: response.label,
    }
  })
  .reset(resetMediaLabels)

export const $isLoadingMediaAttributes = createStore(false, {
  name: '$isLoadingMediaAttributes',
}).on(loadMediaAttributesFx.pending, (_, pending) => pending)

sample({
  clock: loadMediaAttributes,
  target: loadMediaAttributesFx,
})
